/**
 * Pulse wall — the cluster/cell renderers (the bars themselves), in the SHIPPED
 * minimal-glow language (the only Pulse look). One function band = a cluster of
 * glowing bars on a dark stage. Each section carries an ALWAYS-ON, subtle caption
 * (its name + health score) so a band is never a guess; hovering a section lifts
 * it, recedes its neighbours, frames it (a faint panel + a baseline), and brightens
 * that caption. The per-bar cursor-following tip is the shared `WallTipLayer`
 * (pulse-wall-tips). The drill is unchanged: a live bar links out (`href`) or drills
 * in-wall (`onTouch`), and a section card dives via its `group.href` stretched link.
 *
 * Colour comes from ONE source — `pulseBarFill`/`pulseToneRgb` in
 * `pulse-wall-shared` (healthy = green glow, low = warm severity ramp). A blank
 * (uninstrumented) bar is JUST a faint hairline ring — an empty slot that recedes —
 * and fills with its ghost-white colour on hover so you can see which slot you're
 * on. The dark stage + the `group/wall` hover-dim are applied by the wall container
 * in `pulse-wall.tsx`.
 */

import type { ReactNode } from "react";
import { Icon } from "../../icon";
import { cn } from "../../utils";
import { Link } from "../link";
import { TargetChip } from "./pulse-target";
import { usePlayLaunch } from "../play/play-launch-context";
import {
  band,
  BAND,
  bandLight,
  bandState,
  isLive,
  isSuggested,
  pulseBarFill,
  PULSE_BAR_RADIUS,
  PULSE_GHOST_RING,
  type BandLightKind,
  type PulseSize,
  type PulseViewMode,
} from "./pulse-wall-shared";
import type { PulseGroupDTO, PulseTouchDTO } from "../../api/contracts/pulse";

/** The section caption — the band's name + health score, now ALWAYS ON (a subtle
 *  label so a band is never a guess), sitting just beneath the section in the
 *  stage's bottom padding so it never overlaps the bars. It spans the full column
 *  width and truncates (never overlapping a neighbour), reads muted at rest, and
 *  brightens on section hover. The score reads in the band's severity colour when
 *  below target, muted when healthy. Editors' inline target-tune chips stay a
 *  hover-only reveal beneath it (an editing affordance, not always-on chrome).
 *  Shared by both cluster shapes. */
function SectionCaption({
  label,
  health,
  target,
  subjects,
  children,
}: {
  label: string;
  health: number | null;
  target: number;
  /** Portfolio subject count — shown only when > 1 (a single-subject band reads 1). */
  subjects?: number;
  /** Editors' inline target-tune chips, revealed on hover beneath the caption. */
  children?: ReactNode;
}) {
  const below = health != null && health < target;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-full z-30 mt-2 px-1 text-center">
      <span className="inline-flex max-w-full items-center justify-center gap-1 align-middle type-subtext leading-none text-muted-foreground/80 transition-colors duration-150 ease-out group-hover/sec:text-foreground">
        <span className="min-w-0 truncate">{label}</span>
        {subjects != null && subjects > 1 && (
          <span className="shrink-0 tabular-nums text-muted-foreground/55">{subjects}</span>
        )}
        {health != null && (
          <span className={cn("shrink-0 tabular-nums", below ? BAND[band(health, target)].fg : "text-muted-foreground/55")}>
            {health}
          </span>
        )}
      </span>
      {children && (
        <div className="mt-1 flex flex-col items-center gap-1 opacity-0 transition-opacity duration-150 ease-out group-hover/sec:pointer-events-auto group-hover/sec:opacity-100">
          {children}
        </div>
      )}
    </div>
  );
}

/** The hover frame — a faint panel marking the section's extent + a baseline that
 *  grounds it, so "you are on THIS section" reads instantly. The baseline stays lit
 *  (primary) for the current sub-page's section even at rest. */
function SectionFrame({ isCurrent }: { isCurrent?: boolean }) {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-md bg-foreground/[0.05] opacity-0 transition-opacity duration-150 ease-out group-hover/sec:opacity-100"
      />
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 z-20 h-0.5 rounded-full transition-opacity duration-150 ease-out",
          isCurrent
            ? "bg-primary/70 opacity-100"
            : "bg-foreground/55 opacity-0 group-hover/sec:opacity-100",
        )}
      />
    </>
  );
}

/** The bars row — glowing bars, bottom-aligned, lifting as one on section hover. */
function BarsRow({
  bars,
  groupLabel,
  track,
  mini,
  lifted,
  onTouch,
}: {
  bars: PulseTouchDTO[];
  groupLabel: string;
  track: number;
  mini: boolean;
  /** Lift bars above the section's stretched dive-link so a bar-click hits the bar. */
  lifted: boolean;
  onTouch?: (touchId: string) => void;
}) {
  return (
    <div
      // pointer-events-none so the gaps + space above short bars fall through to the
      // section dive-link beneath; lifted bars re-enable their own pointer events.
      className={cn(
        "pointer-events-none relative flex w-full origin-bottom items-end gap-0.5",
        mini ? "" : "transition-transform duration-150 ease-out group-hover/sec:scale-y-[1.03]",
      )}
      style={{ height: track }}
    >
      {bars.map((t) => (
        <Bar
          key={t.id}
          touch={t}
          groupLabel={groupLabel}
          mini={mini}
          lifted={lifted}
          // A bar's own `href` (server-computed drill target, §8) wins — including a
          // navigable GHOST like a measurement-map `node:*` rollup bar (health:null,
          // href set), which must still LINK OUT even though it isn't a live number.
          // Only a live bar WITHOUT an href falls to the in-wall drill (`onTouch`).
          href={t.href}
          onClick={onTouch && isLive(t) && !t.href ? () => onTouch(t.id) : undefined}
        />
      ))}
    </div>
  );
}

function clusterTargetOf(group: PulseGroupDTO): number {
  const liveTargets = group.touches.filter(isLive).map((t) => t.target);
  return liveTargets.length
    ? Math.round(liveTargets.reduce((a, t) => a + t, 0) / liveTargets.length)
    : 70;
}

// ── one function band = a cluster of bars (seamless wall — mini + rollup) ───────

export function AggCluster({
  group,
  first: _first,
  worstId: _worstId,
  track,
  showFooter,
  size,
  mode,
  scope,
  editable,
  view: _view = "house",
  onTouch,
}: {
  group: PulseGroupDTO;
  first: boolean;
  worstId?: string;
  track: number;
  showFooter: boolean;
  size: PulseSize;
  mode: string;
  scope: string;
  editable?: boolean;
  view?: PulseViewMode;
  onTouch?: (touchId: string) => void;
}) {
  const gh = group.health;
  const clusterTarget = clusterTargetOf(group);
  const bars = group.touches.filter((t) => !isSuggested(t));
  const empty = bandState(group) === "uninstrumented";
  const mini = size === "mini";
  const showCaption = showFooter && !mini;

  return (
    <div
      data-pulse-cluster={group.key}
      className={cn(
        "group/sec relative flex min-w-0 flex-col",
        !mini && "transition-opacity duration-150 ease-out group-hover/wall:opacity-40 hover:opacity-100!",
      )}
      // Every band — live or uninstrumented — weights by its bar count, so an empty
      // band's ring-wells line up with a live neighbour's bars (no fixed-width door).
      style={{ flexGrow: Math.max(bars.length, 1), flexBasis: 0 }}
    >
      {empty ? (
        // An uninstrumented band belongs to the wall now: its planned touches draw as
        // empty ring-wells + a hover "Set up" caption, not a dashed door. A tiny mini
        // embed keeps the compact door — a ring row is too small to read there.
        mini ? (
          <EmptyDoor group={group} size={size} track={track} />
        ) : (
          <EmptyWells group={group} track={track} size={size} />
        )
      ) : (
        <>
          {!mini && <SectionFrame />}
          <BarsRow bars={bars} groupLabel={group.label} track={track} mini={mini} lifted={false} onTouch={onTouch} />
          {showCaption && (
            <SectionCaption label={group.label} health={gh} target={clusterTarget}>
              {editable &&
                bars.filter(isLive).map((t) => (
                  <TargetChip
                    key={t.id}
                    mode={mode}
                    groupKey={group.key}
                    touchId={t.id}
                    target={t.target}
                    calibration={t.calibration}
                    trend={t.trend}
                    scope={scope}
                    editable
                  />
                ))}
            </SectionCaption>
          )}
        </>
      )}
    </div>
  );
}

// ── a SECTION rendered as one clickable column (treatment C — the card wall) ────
//
// The full mode-dashboard wall renders each LIVE section as a seamless column on
// the dark stage that dives into the section home (`group.href`), while the bars
// stay independently interactive. The card carries a full-bleed stretched <Link>
// beneath everything; the bars sit `lifted` above it, so a bar-click hits the bar
// (its in-wall drill + tooltip) and a click anywhere else falls through to the
// section link. Hover lifts + frames the section and reveals its caption.

export function ClusterCard({
  group,
  worstId: _worstId,
  track,
  compact,
  isCurrent,
  onTouch,
}: {
  group: PulseGroupDTO;
  worstId?: string;
  track: number;
  compact?: boolean;
  isCurrent?: boolean;
  onTouch?: (touchId: string) => void;
}) {
  const gh = group.health;
  const clusterTarget = clusterTargetOf(group);
  const bars = group.touches.filter((t) => !isSuggested(t));
  const subjects = group.touches.reduce((m, t) => Math.max(m, t.activeCount), 0);

  return (
    <div
      data-pulse-cluster={group.key}
      className="group/sec relative flex min-w-0 flex-col transition-opacity duration-150 ease-out group-hover/wall:opacity-40 hover:opacity-100!"
      // Every section on the card wall is the SAME width.
      style={{ flexBasis: 0, flexGrow: 1 }}
    >
      {/* The stretched section-page link — covers the whole column, beneath the
          lifted bars. IA-agnostic: the block only follows `group.href`. Guard the
          href: a `<Link href={undefined}>` renders an <a> with NO href — a dead,
          non-navigable surface that still covers the column and SWALLOWS every
          click (the reported "clicking the section doesn't navigate"). When the
          server set no door (a lane/entity scope), render nothing so clicks fall
          through to the bars / underlying surface instead. */}
      {group.href ? (
        <Link
          href={group.href}
          variant="unstyled"
          aria-label={`Open ${group.label}`}
          title={`Open ${group.label}`}
          className="absolute inset-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        />
      ) : null}
      <SectionFrame isCurrent={isCurrent} />
      <BarsRow
        bars={bars}
        groupLabel={group.label}
        track={track}
        mini={false}
        // On the compact sub-page card the bars are NOT lifted — every click
        // (bars included) falls through to the section link, matching the old
        // strip's whole-band navigation.
        lifted={!compact}
        onTouch={compact ? undefined : onTouch}
      />
      <SectionCaption label={group.label} health={gh} target={clusterTarget} subjects={subjects} />
    </div>
  );
}

/** The on-ramp label + icon for lighting an uninstrumented band, keyed by the
 *  `bandLight` kind. Shared by EmptyWells (full/strip) + EmptyDoor (mini). */
const LIGHT_META: Record<BandLightKind, { label: string; icon: string }> = {
  connect: { label: "Connect a source", icon: "plug" },
  enable: { label: "Set up pipeline", icon: "sliders-horizontal" },
  activate: { label: "Set it up", icon: "sparkles" },
};

/** An uninstrumented section in card-wall mode — the SAME seamless column as a live
 *  one, but its body is a row of empty ring-wells + a hover "Set up" caption (via
 *  EmptyWells) in place of live bars, so it belongs to the wall instead of punching
 *  a dashed hole in it. */
export function EmptyCell({
  group,
  track,
  compact,
  isCurrent,
}: {
  group: PulseGroupDTO;
  track: number;
  compact?: boolean;
  isCurrent?: boolean;
}) {
  return (
    <div
      data-pulse-cluster={group.key}
      className="group/sec relative flex min-w-0 flex-col transition-opacity duration-150 ease-out group-hover/wall:opacity-40 hover:opacity-100!"
      style={{ flexBasis: 0, flexGrow: 1 }}
    >
      <EmptyWells group={group} track={track} size={compact ? "strip" : "full"} isCurrent={isCurrent} />
    </div>
  );
}

/** An uninstrumented section, redrawn to BELONG to the wall: its planned touches
 *  render as empty ring-wells (the same bar language as a live band) instead of a
 *  dashed "door" that punched a hole in the wall. The whole column is the setup
 *  on-ramp (a Play-focused launch, else the band's setup route); the caption reads
 *  the section name at rest and reveals a primary "· Set up →" on hover. Used on the
 *  full + strip walls (a tiny mini embed keeps the compact `EmptyDoor` icon). */
function EmptyWells({
  group,
  track,
  size,
  isCurrent,
}: {
  group: PulseGroupDTO;
  track: number;
  size: PulseSize;
  isCurrent?: boolean;
}) {
  const wells = group.touches.filter((t) => !isSuggested(t));
  const light = bandLight(group);
  const meta = light ? LIGHT_META[light.kind] : { label: "Set it up", icon: "sparkles" };
  const { launch, available } = usePlayLaunch();
  // The whole column is the setup on-ramp, beneath the (non-interactive) ring-wells:
  // a click anywhere on the band starts setup, mirroring the old door's whole-body
  // click. Same stretched-link treatment a live ClusterCard uses.
  const rampCls = "absolute inset-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  return (
    <>
      {available ? (
        <button
          type="button"
          onClick={() => launch({ sectionSlug: group.domain.area })}
          aria-label={meta.label}
          title={meta.label}
          className={rampCls}
        />
      ) : (
        <Link href={light?.href ?? "/integrations"} variant="unstyled" aria-label={meta.label} title={meta.label} className={rampCls} />
      )}
      <SectionFrame isCurrent={isCurrent} />
      {wells.length ? (
        <BarsRow bars={wells} groupLabel={group.label} track={track} mini={size === "mini"} lifted={false} />
      ) : (
        // No planned touches to draw (all-suggested / none) → a single empty ring so
        // the band still reads as an empty slot, not a void.
        <div className="pointer-events-none flex w-full items-end" style={{ height: track }}>
          {/* radius-ignore: pulse instrument-wall bar radius (shared PULSE_BAR_RADIUS token) — proportional data-viz, not a UI surface. */}
          <span className="h-full w-full" style={{ borderRadius: PULSE_BAR_RADIUS, boxShadow: PULSE_GHOST_RING }} />
        </div>
      )}
      <SetupCaption label={group.label} />
    </>
  );
}

/** The empty section's caption — the section name, always on (like a live band's),
 *  revealing a primary "· Set up →" on hover: the quiet label + an earned
 *  invitation, replacing the old dashed door's inline CTA. */
function SetupCaption({ label }: { label: string }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-full z-30 mt-2 px-1 text-center">
      <span className="inline-flex max-w-full items-center justify-center gap-1 align-middle type-subtext leading-none text-muted-foreground/80 transition-colors duration-150 ease-out group-hover/sec:text-primary">
        <span className="min-w-0 truncate">{label}</span>
        <span className="hidden shrink-0 items-center gap-0.5 group-hover/sec:inline-flex">
          <span aria-hidden>·</span>Set up
          <Icon name="arrow-right" size="xs" />
        </span>
      </span>
    </div>
  );
}

// ── an uninstrumented section: empty ring-wells (full/strip) or a compact door ──
//
// A band with no live instrument carries an on-ramp (Connect a source / Set up
// pipeline / Set it up) that launches Play focused on this section, falling back to
// the band's setup route when no launcher is mounted (the styleguide / a standalone
// embed). On the full + strip walls it renders as `EmptyWells`; a tiny mini embed
// keeps the compact `EmptyDoor` icon below.

function EmptyDoor({
  group,
  size,
  track,
  bare,
}: {
  group: PulseGroupDTO;
  size: PulseSize;
  track: number;
  bare?: boolean;
}) {
  const light = bandLight(group);
  const meta = light ? LIGHT_META[light.kind] : { label: "Set it up", icon: "sparkles" };
  const { launch, available } = usePlayLaunch();

  const panelCls = bare
    ? "group/door flex w-full flex-col items-center justify-center gap-2 rounded-md px-2 text-center transition-colors hover:bg-foreground/[0.04]"
    : "group/door flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-foreground/15 px-2 text-center transition-colors hover:border-primary/40 hover:bg-foreground/[0.04]";

  const body =
    size === "mini" ? (
      <Icon name={meta.icon} size="sm" className="text-muted-foreground" />
    ) : size === "strip" ? (
      <>
        <Icon name={meta.icon} size="base" className="text-muted-foreground transition-colors group-hover/door:text-primary" />
        <span className="type-subtext font-medium text-foreground">{meta.label}</span>
      </>
    ) : (
      <>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground/10 text-muted-foreground transition-colors group-hover/door:bg-primary/15 group-hover/door:text-primary">
          <Icon name={meta.icon} size="base" />
        </span>
        <span className="type-subtext font-medium text-foreground">{meta.label}</span>
        <span className="inline-flex items-center gap-0.5 type-subtext text-primary/80 transition-transform group-hover/door:translate-x-0.5">
          Start <Icon name="arrow-right" size="xs" />
        </span>
      </>
    );

  return available ? (
    <button
      type="button"
      onClick={() => launch({ sectionSlug: group.domain.area })}
      className={panelCls}
      style={{ height: track }}
    >
      {body}
    </button>
  ) : (
    <Link href={light?.href ?? "/integrations"} variant="unstyled" className={panelCls} style={{ height: track }}>
      {body}
    </Link>
  );
}

// ── one bar — the glow paint (warm ramp / green / faint-white ghost) ───────────
//
// The bar is JUST the trigger surface, tagged `data-pulse-bar={id}`; the hover
// detail is drawn ONCE for the whole wall by the cursor-following <WallTipLayer>.

/** The blank-bar ring style, from the ONE shared source (`pulse-wall-shared`). */
// radius-ignore: pulse instrument-wall bar radius (shared PULSE_BAR_RADIUS token) — proportional data-viz, not a UI surface.
const GHOST_WELL_STYLE = { borderRadius: PULSE_BAR_RADIUS, boxShadow: PULSE_GHOST_RING } as const;

function Bar({
  touch,
  groupLabel,
  mini: _mini,
  href,
  onClick,
  lifted,
}: {
  touch: PulseTouchDTO;
  groupLabel: string;
  mini: boolean;
  href?: string;
  onClick?: () => void;
  lifted?: boolean;
}) {
  const ghost = touch.health == null;
  const fill = pulseBarFill(touch.health, touch.target);

  const inner = ghost ? (
    // A blank bar — JUST a hairline ring (an empty slot that recedes). On hover it
    // fills with its original ghost-white colour so you can see which slot you're on.
    <span className="relative h-full w-full" style={GHOST_WELL_STYLE}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-150 ease-out group-hover/bar:opacity-100"
        // radius-ignore: pulse instrument-wall bar radius (shared PULSE_BAR_RADIUS token) — proportional data-viz, not a UI surface.
        style={{ borderRadius: PULSE_BAR_RADIUS, background: fill.background }}
      />
    </span>
  ) : (
    <span
      className="relative z-20 w-full rounded-xs transition-[filter] duration-150 group-hover/bar:brightness-110"
      style={{ height: `${Math.max(touch.health as number, 4)}%`, background: fill.background, boxShadow: fill.boxShadow }}
    />
  );

  // A bar that owns a click target (its own `href`, or the in-wall `onClick` drill)
  // must catch its own pointer events even when the row isn't `lifted` — otherwise
  // the section-background link beneath swallows the click and the bar never fires.
  const interactive = !!href || (!!onClick && !ghost);
  const cls = `group/bar relative flex h-full min-w-[5px] flex-1 items-end${
    lifted || interactive ? " z-10 pointer-events-auto" : ""
  }`;
  const label = `${groupLabel} · ${touch.label}: ${
    ghost ? "not instrumented" : `${touch.health} vs target ${touch.target}`
  }`;
  // An `href` wins — a live bar's drill target OR a navigable ghost (`node:*`
  // measurement-map rollup, health:null but server-set href). It links out either
  // way; only a live bar with no href falls to the in-wall `onClick` drill.
  return href ? (
    <Link href={href} variant="unstyled" aria-label={label} data-pulse-bar={touch.id} className={cls}>
      {inner}
    </Link>
  ) : onClick && !ghost ? (
    <button type="button" aria-label={label} data-pulse-bar={touch.id} onClick={onClick} className={cls}>
      {inner}
    </button>
  ) : (
    <span data-pulse-bar={touch.id} className={cls}>
      {inner}
    </span>
  );
}
